import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Drawer, Descriptions, Table, Tag, Tabs, Divider } from "antd";
import { ScheduleOutlined, ClockCircleOutlined } from "@ant-design/icons";
import { scheduleService } from "../../../../api/enhancedApi";
import FlightTimeDisplay from "../../../../components/DateTimeDisplay";
import FlightDateDisplay from "../../../../components/DateTimeDisplay/FlightDateDisplay";
import GeneralEmailEditor from "../../../../components/EmailEditor/GeneralEmailEditor";
import FlightCompare from "../../ScheduleEditor/components/FlightCompare";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

// 导入新的子组件和工具
import ScheduleStatusActions from "./ScheduleStatusActions";
import SchedulePublishDrawer from "./SchedulePublishDrawer";
import ScheduleConfirmDrawer from "./ScheduleConfirmDrawer";
import PublishEmailSendDrawer from "./PublishEmailSendDrawer";
import ScheduleActivityTimeline from "./ScheduleActivityTimeline";
import TemplateSelectionModal from "./TemplateSelectionModal";
import UnifiedEmailSendDrawer from "./UnifiedEmailSendDrawer";
import { useScheduleActions } from "../hooks/useScheduleActions";
import { getStatusColor, getStatusText } from "../utils/scheduleUtils";
import ScheduleTag from "../../shared/ScheduleTag";
import { useScheduleTags } from "../../shared/useScheduleTags";
import RotationVisualizer from "../../../flightOperation/Monitor/components/RotationVisualizer";

// 引入dayjs插件
dayjs.extend(utc);

const { TabPane } = Tabs;

/**
 * 计划详情抽屉组件（重构后的简化版本）
 */
const ScheduleDetailDrawer = ({ visible, onClose, schedule, onStatusChange }) => {
    const [flights, setFlights] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentSchedule, setCurrentSchedule] = useState(schedule); // 本地状态管理最新的schedule
    const { definitionMap: scheduleTagDefinitionMap, loadError: scheduleTagLoadError } = useScheduleTags({ includeInactive: true });

    // 对比预览相关状态
    const [previewManualMatchings, setPreviewManualMatchings] = useState([]);
    const previewCompareRef = useRef(null);

    // 统一邮件发送抽屉状态
    const [quickMode, setQuickMode] = useState(false); // 快速模式状态

    // 重新加载schedule数据的函数
    const reloadScheduleData = useCallback(async () => {
        if (!schedule?.id) return;

        try {
            const response = await scheduleService.getScheduleDetail(schedule.id);
            const updatedSchedule = response?.data;
            if (updatedSchedule) {
                setCurrentSchedule(updatedSchedule);
            }
        } catch (error) {
            console.error("重新加载计划数据失败:", error);
        }
    }, [schedule?.id]);

    // 包装 onStatusChange，在状态变化后重新加载数据
    const handleStatusChangeWithReload = useCallback(async () => {
        // 先调用外层的刷新
        onStatusChange && onStatusChange();
        // 然后重新加载抽屉内的数据
        await reloadScheduleData();
    }, [onStatusChange, reloadScheduleData]);

    // 使用自定义Hook管理状态操作，传入包装后的回调
    // 注意：这里传入handleStatusChangeWithReload作为refreshSchedules参数，
    // 因为它既会刷新外层列表也会重新加载抽屉内的数据
    const scheduleActions = useScheduleActions(currentSchedule, handleStatusChangeWithReload, handleStatusChangeWithReload);

    // 加载航班数据
    const loadFlights = async () => {
        if (!currentSchedule?.id) return;

        try {
            setLoading(true);
            const response = await scheduleService.getScheduleFlights(currentSchedule.id, {
                page: 1,
                limit: 1000,
            });

            // 确保flights始终是一个数组
            let flightsData = [];
            if (response?.data?.items && Array.isArray(response.data.items)) {
                flightsData = response.data.items;
            } else if (response?.data && Array.isArray(response.data)) {
                flightsData = response.data;
            } else if (response?.items && Array.isArray(response.items)) {
                flightsData = response.items;
            } else if (Array.isArray(response)) {
                flightsData = response;
            } else {
                console.warn("无法识别的API响应格式:", response);
                flightsData = [];
            }

            setFlights([...flightsData].sort((left, right) => new Date(left.std || 0) - new Date(right.std || 0)));
        } catch (error) {
            console.error("加载航班数据失败:", error);
            setFlights([]); // 确保在错误情况下设置为空数组
        } finally {
            setLoading(false);
        }
    };

    // 加载对比预览的已保存匹配数据
    const loadPreviewMatchings = useCallback(async () => {
        if (!currentSchedule?.id) return;

        try {
            // 加载计划详情，获取已保存的匹配数据
            const detailResponse = await scheduleService.getScheduleDetail(currentSchedule.id, { limit: 1 });
            const scheduleDetail = detailResponse?.data;
            const parameterData = scheduleDetail?.parameter;

            let savedMatchings = [];

            // 检查并提取已保存的匹配数据
            if (parameterData?.parameter_json?.flight_compare_result) {
                const savedData = parameterData.parameter_json.flight_compare_result;
                if (savedData.manualMatchings) {
                    savedMatchings = savedData.manualMatchings;
                    setPreviewManualMatchings(savedMatchings);
                }
            }

            // 加载对比组件数据
            if (previewCompareRef.current) {
                previewCompareRef.current.loadCurrentOperations(savedMatchings);
            }
        } catch (error) {
            console.error("加载对比预览匹配数据失败:", error);
            // 加载失败时仍然尝试正常加载对比
            if (previewCompareRef.current) {
                previewCompareRef.current.loadCurrentOperations();
            }
        }
    }, [currentSchedule?.id]);

    // 当抽屉打开时加载数据，关闭时清理数据
    useEffect(() => {
        if (visible && currentSchedule?.id) {
            loadFlights();
        } else if (!visible) {
            // 抽屉关闭时清理数据，避免数据残留
            setFlights([]);
        }
    }, [visible, currentSchedule?.id]);

    // 当外层schedule prop变化时，更新本地状态
    useEffect(() => {
        if (schedule) {
            setCurrentSchedule(schedule);
        }
    }, [schedule]);

    // 当对比预览抽屉打开时加载匹配数据
    useEffect(() => {
        if (scheduleActions.comparePreviewVisible && currentSchedule?.id) {
            loadPreviewMatchings();
        }
    }, [scheduleActions.comparePreviewVisible, currentSchedule?.id, loadPreviewMatchings]);

    // 航班表格列定义
    const flightColumns = useMemo(
        () => [
            // 序号
            {
                title: "序号",
                dataIndex: "index",
                key: "index",
                width: 60,
                render: (text, record, index) => index + 1,
            },
            {
                title: "航班号",
                dataIndex: "flight_number",
                key: "flight_number",
                width: 100,
            },
            {
                title: "ROT",
                key: "rotation",
                width: 48,
                align: "center",
                render: (_, record) => <RotationVisualizer flight={record} allFlights={flights} />,
            },
            {
                title: "日期",
                dataIndex: "flight_date",
                key: "flight_date",
                width: 120,
                render: (text, record) => {
                    return <FlightDateDisplay etdOrStd={record.std} departureAirportCode={record.departure_airport} originalFlightDateForFallback={record.flight_date} />;
                },
            },
            {
                title: "航线",
                key: "route",
                width: 100,
                render: (_, record) => `${record.departure_airport || ""} → ${record.arrival_airport || ""}`,
            },
            {
                title: "起飞时间",
                dataIndex: "std",
                key: "std",
                width: 100,
                render: (text, record) => (text ? <FlightTimeDisplay utcTime={text} airportCode={record.departure_airport} format="HH:mm" /> : "-"),
            },
            {
                title: "到达时间",
                dataIndex: "sta",
                key: "sta",
                width: 100,
                render: (text, record) => (text ? <FlightTimeDisplay utcTime={text} airportCode={record.arrival_airport} format="HH:mm" /> : "-"),
            },
            {
                title: "飞机",
                dataIndex: "aircraft_reg",
                key: "aircraft_reg",
                width: 100,
                render: (text) => text || "-",
            },
            {
                title: "航班性质",
                dataIndex: "stc_type",
                key: "stc_type",
                width: 90,
                render: (text) => text || "-",
            },
            {
                title: "备注",
                dataIndex: "remarks",
                key: "remarks",
                ellipsis: true,
                render: (text) => text || "-",
            },
        ],
        [flights],
    );

    // 计划基本信息
    const scheduleInfo = useMemo(() => {
        if (!currentSchedule) return [];

        return [
            {
                key: "name",
                label: "计划名称",
                children: currentSchedule.name,
            },
            {
                key: "status",
                label: "状态",
                children: <Tag color={getStatusColor(currentSchedule.status)}>{getStatusText(currentSchedule.status)}</Tag>,
            },
            {
                key: "period",
                label: "计划期间",
                children: `${dayjs(currentSchedule.start_date).format("YYYY-MM-DD")} 至 ${dayjs(currentSchedule.end_date).format("YYYY-MM-DD")}`,
            },
            {
                key: "description",
                label: "描述",
                children: currentSchedule.description || "-",
            },
            {
                key: "tags",
                label: "标签",
                children:
                    currentSchedule.tags?.length > 0
                        ? currentSchedule.tags.map((tag, index) => (
                              <ScheduleTag
                                  key={`${tag}-${index}`}
                                  name={tag}
                                  definitionMap={scheduleTagDefinitionMap}
                                  loadError={scheduleTagLoadError}
                              />
                          ))
                        : "-",
            },
            {
                key: "created_at",
                label: "创建时间",
                children: dayjs(currentSchedule.created_at).format("YYYY-MM-DD HH:mm:ss"),
            },
            {
                key: "updated_at",
                label: "更新时间",
                children: dayjs(currentSchedule.updated_at).format("YYYY-MM-DD HH:mm:ss"),
            },
        ];
    }, [currentSchedule, scheduleTagDefinitionMap, scheduleTagLoadError]);

    // 邮件编辑器的上下文数据 - V2模式格式
    const emailContextData = useMemo(() => {
        // 简化的上下文数据，不再需要对比结果
        const contextData = {
            // V1模式兼容格式（保留原有结构）
            schedule: currentSchedule || {},
            flights: Array.isArray(flights) ? flights : [],

            // V2模式需要的contentBlocks格式
            contentBlocks: [
                {
                    id: "flight_list", // 统一使用 flight_list 作为ID，匹配模板
                    type: "scheduleFlightList", // 使用标准航班列表类型
                    title: "计划航班列表",
                    data: Array.isArray(flights) ? flights : [],
                    // 添加正确的business_source信息
                    business_source: {
                        entity_type: "ScheduleFlight",
                        entity_ids: Array.isArray(flights) ? flights.filter((flight) => flight.id).map((flight) => flight.id) : [],
                    },
                    // 为Excel导出添加额外的上下文信息
                    schedule_info: currentSchedule || {},
                },
            ],
        };

        // 添加调试信息
        console.log("[ScheduleDetailDrawer] 生成邮件上下文数据:", {
            currentSchedule: currentSchedule,
            flights: flights,
            flightsCount: Array.isArray(flights) ? flights.length : 0,
            contextData: contextData,
        });

        return contextData;
    }, [currentSchedule, flights]);

    return (
        <>
            <Drawer title={`计划详情: ${currentSchedule?.name || "未命名计划"}`} placement="right" width="70%" onClose={onClose} open={visible} destroyOnHidden={true}>
                <div className="schedule-detail-content">
                    {/* 状态操作区域 */}
                    <ScheduleStatusActions
                        schedule={currentSchedule}
                        handlers={{
                            ...scheduleActions,
                            handleSendPublishEmail: () => {
                                setQuickMode(false);
                                scheduleActions.handleOpenUnifiedEmailDrawer("publish");
                            },
                            handleQuickSendPublishEmail: () => {
                                setQuickMode(true);
                                scheduleActions.handleOpenUnifiedEmailDrawer("publish");
                            },
                        }}
                        isEmailLoading={scheduleActions.isEmailLoading}
                    />

                    {/* 计划信息 */}
                    <div className="schedule-info-section">
                        <Descriptions title="计划信息" bordered column={2} size="small" items={scheduleInfo} />
                    </div>

                    <Divider />

                    {/* 航班数据和活动记录 */}
                    <div className="flights-section">
                        <Tabs defaultActiveKey="flights">
                            <TabPane
                                tab={
                                    <span>
                                        <ScheduleOutlined />
                                        航班明细 ({flights.length})
                                    </span>
                                }
                                key="flights"
                            >
                                {flights.length === 0 && !loading ? (
                                    <div style={{ textAlign: "center", padding: "20px 0" }}>暂无航班数据</div>
                                ) : (
                                    <>
                                        <Table columns={flightColumns} dataSource={flights} rowKey="id" loading={loading} scroll={{ x: 800 }} size="small" sticky={{ offsetHeader: -18 }} pagination={false} />
                                    </>
                                )}
                            </TabPane>

                            <TabPane
                                tab={
                                    <span>
                                        <ClockCircleOutlined />
                                        活动记录
                                    </span>
                                }
                                key="activity-timeline"
                            >
                                <ScheduleActivityTimeline scheduleId={currentSchedule?.id} />
                            </TabPane>
                        </Tabs>
                    </div>
                </div>
            </Drawer>

            {/* 邮件编辑器 */}
            {scheduleActions.isEmailEditorVisible && scheduleActions.emailTemplate && (
                <GeneralEmailEditor
                    visible={scheduleActions.isEmailEditorVisible}
                    onSubmit={scheduleActions.handleEmailSubmit}
                    onCancel={scheduleActions.handleEmailCancel}
                    title="发送航班计划确认邮件"
                    subjectTemplate={scheduleActions.emailTemplate?.subject_template || ""}
                    initialSubject={`航班计划确认 - ${currentSchedule?.name || "未命名计划"}`}
                    templateContent={scheduleActions.emailTemplate?.body_html || ""}
                    templateRecipients={{
                        default_to: scheduleActions.emailTemplate?.default_to,
                        default_cc: scheduleActions.emailTemplate?.default_cc,
                    }}
                    businessContext={emailContextData}
                    enableV2Mode={true}
                />
            )}

            {/* 确认抽屉 */}
            <ScheduleConfirmDrawer visible={scheduleActions.confirmDrawerVisible} onClose={() => scheduleActions.setConfirmDrawerVisible(false)} schedule={{ ...currentSchedule, flights }} onConfirm={scheduleActions.handleConfirmSuccess} />

            {/* 发布抽屉 */}
            <SchedulePublishDrawer visible={scheduleActions.publishDrawerVisible} onClose={() => scheduleActions.setPublishDrawerVisible(false)} schedule={{ ...currentSchedule, flights }} onPublish={scheduleActions.handlePublishSuccess} />

            {/* 发布邮件发送抽屉 */}
            <PublishEmailSendDrawer visible={scheduleActions.publishEmailDrawerVisible} onClose={() => scheduleActions.setPublishEmailDrawerVisible(false)} schedule={currentSchedule} onSuccess={scheduleActions.handlePublishEmailSuccess} />

            {/* 对比预览抽屉 */}
            {scheduleActions.comparePreviewVisible && (
                <Drawer title={`对比预览: ${currentSchedule?.name || "未命名计划"}`} placement="right" width="95%" onClose={() => scheduleActions.setComparePreviewVisible(false)} open={scheduleActions.comparePreviewVisible} destroyOnHidden={true}>
                    <div className="compare-preview-content">
                        <div className="compare-preview-instructions" style={{ marginBottom: 16 }}>
                            <div style={{ padding: "12px", background: "#f0f8ff", border: "1px solid #d4edda", borderRadius: "4px" }}>
                                <h4 style={{ margin: "0 0 8px 0", color: "#0c5460" }}>对比预览模式</h4>
                                <p style={{ margin: "0 0 8px 0", color: "#0c5460" }}>在此模式下，您可以：</p>
                                <ul style={{ margin: "0", paddingLeft: "20px", color: "#0c5460" }}>
                                    <li>查看当前计划与执行计划的对比结果</li>
                                    <li>进行手动匹配操作和调整</li>
                                    <li>预览发布后的变更效果</li>
                                </ul>
                                <p style={{ margin: "8px 0 0 0", color: "#666", fontSize: "12px" }}>注意：此处的操作仅用于预览，不会保存到计划中。如需正式操作，请使用相应的确认或发布功能。</p>
                            </div>
                        </div>
                        <FlightCompare
                            ref={previewCompareRef}
                            scheduleData={{ schedule: currentSchedule, flights }}
                            onMatchingsChange={() => {}} // 预览模式不保存匹配
                            onCompareResult={() => {}} // 预览模式不保存结果
                            manualMatchings={previewManualMatchings}
                        />
                    </div>
                </Drawer>
            )}

            {/* 模板选择对话框 */}
            <TemplateSelectionModal visible={scheduleActions.templateSelectionVisible} onSelect={scheduleActions.handleTemplateSelection} onCancel={scheduleActions.handleCancelTemplateSelection} />

            {/* 统一邮件发送抽屉 */}
            <UnifiedEmailSendDrawer
                visible={scheduleActions.unifiedEmailDrawerVisible}
                onClose={() => {
                    scheduleActions.handleCloseUnifiedEmailDrawer();
                    setQuickMode(false); // 关闭时重置快速模式
                }}
                schedules={currentSchedule ? [currentSchedule] : []}
                emailType={scheduleActions.unifiedEmailType}
                onSuccess={scheduleActions.handleUnifiedEmailSuccess}
                quickMode={quickMode}
                defaultTemplateCode={scheduleActions.unifiedEmailType === "confirmation" ? "SCHEDULE_AAE_NEW_CONFIRMATION" : "SCHEDULE_OUTSTATION_NEW_PUBLICATION"}
                defaultSplitSending={false}
            />
        </>
    );
};

export default ScheduleDetailDrawer;
